import React, { useContext } from "react";
import { Box, Typography, Button, Link, Divider } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareItWithUsBackground from '../../src/media/pictures/ShareItWithUsBackground.png';
import { LayoutContext } from "../contexts/LayoutContext/LayoutContext";
import { OgMetaTagsPages } from "./ReusableComponents/OgMetaTags";
import { SocialMediaIconsRow } from "./ReusableComponents/SocialMediaIconsRow";
import {citsEmailAddress, citsMailtoEmailAddress, citsSwissTransferFileTransfer, citsWhatsAppChannel} from "../config";

export const ShareItWithUs = ({ pageMeta }) => {
    const { isMobile, isTablet } = useContext(LayoutContext);

    // Check if desktop (not mobile and not tablet)
    const isDesktop = !isMobile && !isTablet;

    // Social media links - update these with your actual URLs
    const socialLinks = {
        whatsapp: citsWhatsAppChannel,
        email: citsMailtoEmailAddress,
        upload: citsSwissTransferFileTransfer
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden',
                minHeight: '100vh',
                backgroundColor: '#000',
            }}
        >
            <OgMetaTagsPages content={pageMeta} />

            {/* Background Image Section - Full width, 16:9 visible */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    position: 'relative',
                    backgroundColor: '#000',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${ShareItWithUsBackground})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: isMobile ? 'cover' : 'contain',
                        backgroundPosition: 'center',
                        backgroundColor: '#000',
                    }}
                >
                    {/* Title text over image */}
                    <Typography
                        variant={isMobile ? "h4" : isDesktop ? "h1" : "h2"}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#fff',
                            fontWeight: 'bold',
                            textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                            textAlign: 'center',
                            padding: 3,
                            zIndex: 1,
                            marginTop: isMobile ? -5 : isDesktop ? -15 : -10,
                            width: isMobile ? '90%' : 'auto',
                            fontSize: isDesktop ? '4.5rem' : 'auto',
                        }}
                    >
                        Share Your Moments
                    </Typography>
                </Box>
            </Box>

            {/* Content Section - With dark overlay */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: isMobile ? 1 : isDesktop ? 6 : 4,
                    marginTop: isMobile ? '-10px' : isDesktop ? '-60px' : '-40px',
                    position: 'relative',
                    zIndex: 2,
                }}
            >
                <Box
                    sx={{
                        width: isMobile ? '100%' : '100%',
                        maxWidth: isMobile ? '100%' : {
                            sm: '95%',
                            md: '90%',
                            lg: '85%',
                            xl: '80%'
                        },
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        borderRadius: isMobile ? 2 : isDesktop ? 5 : 4,
                        padding: isMobile ? 2 : isDesktop ? 8 : 6,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                >
                    {/* Header Section */}
                    <Box sx={{ textAlign: 'center', width: '100%', mb: isMobile ? 2 : isDesktop ? 6 : 4 }}>
                        <Typography
                            variant={isMobile ? "h5" : isDesktop ? "h1" : "h2"}
                            sx={{
                                color: '#fff',
                                fontWeight: 'bold',
                                mb: isDesktop ? 3 : 2,
                                fontSize: isDesktop ? '3.5rem' : isMobile ? '1.8rem' : 'auto'
                            }}
                        >
                            📸 Share your photos & videos
                        </Typography>
                        <Typography
                            variant={isMobile ? "body1" : isDesktop ? "h3" : "h5"}
                            sx={{
                                color: '#e0e0e0',
                                fontStyle: 'italic',
                                fontSize: isDesktop ? '2.5rem' : isMobile ? '1rem' : 'auto'
                            }}
                        >
                            We'd love to see the night through your eyes!
                        </Typography>
                    </Box>

                    {/* Upload Section */}
                    <Box sx={{ width: '100%', mb: isMobile ? 2 : isDesktop ? 6 : 4 }}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: isMobile ? 2 : isDesktop ? 4 : 3,
                                padding: isMobile ? 2 : isDesktop ? 6 : 4,
                                border: '1px dashed rgba(255, 255, 255, 0.2)',
                                textAlign: 'center',
                                width: '100%'
                            }}
                        >
                            <Typography
                                variant={isMobile ? "h6" : isDesktop ? "h2" : "h4"}
                                sx={{
                                    color: '#fff',
                                    mb: isDesktop ? 3 : 2,
                                    fontSize: isDesktop ? '3rem' : isMobile ? '1.3rem' : 'auto'
                                }}
                            >
                                ⬆️ Upload your photos & videos
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#d0d0d0',
                                    mb: isDesktop ? 4 : 3,
                                    lineHeight: isDesktop ? 2 : 1.8,
                                    fontSize: isDesktop ? '1.4rem' : isMobile ? '0.85rem' : '1.1rem',
                                    width: '100%',
                                    maxWidth: isDesktop ? '80%' : '100%',
                                    mx: 'auto'
                                }}
                            >
                                Tag us on social media to share your favourite moments with everyone, or send us the original high-quality photos and videos below. We may feature them in future Camping In The Skies content (with credit where possible).
                            </Typography>

                            {/* Upload Button */}
                            <Button
                                variant="contained"
                                startIcon={<UploadFileIcon />}
                                href={socialLinks.upload}
                                target="_blank"
                                rel="noopener noreferrer"
                                fullWidth={isMobile}
                                sx={{
                                    backgroundColor: '#4CAF50',
                                    color: '#fff',
                                    padding: isMobile ? '10px 16px' : isDesktop ? '18px 50px' : '14px 40px',
                                    fontSize: isDesktop ? '1.3rem' : isMobile ? '0.85rem' : '1.1rem',
                                    borderRadius: isMobile ? 2 : isDesktop ? 4 : 3,
                                    '&:hover': {
                                        backgroundColor: '#45a049',
                                        transform: 'scale(1.02)',
                                        transition: 'all 0.3s ease'
                                    },
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    mb: isDesktop ? 3 : 2,
                                    width: isMobile ? '100%' : 'auto',
                                    minWidth: isDesktop ? '350px' : 'auto'
                                }}
                            >
                                Upload Files (Swisstransfer)
                            </Button>

                            {/* Email Link */}
                            <Box sx={{
                                mt: isDesktop ? 3 : 2,
                                width: '100%',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: isMobile ? 1 : isDesktop ? 2 : 0
                            }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#b0b0b0',
                                        display: 'inline',
                                        mr: isMobile ? 0 : isDesktop ? 2 : 1,
                                        fontSize: isDesktop ? '1.3rem' : isMobile ? '0.85rem' : '1rem'
                                    }}
                                >
                                    Send to:
                                </Typography>
                                <Link
                                    href={socialLinks.email}
                                    sx={{
                                        color: '#64B5F6',
                                        textDecoration: 'none',
                                        fontWeight: 'bold',
                                        fontSize: isDesktop ? '1.3rem' : isMobile ? '0.85rem' : '1rem',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            color: '#90CAF9'
                                        }
                                    }}
                                >
                                    {citsEmailAddress}
                                </Link>
                            </Box>
                        </Box>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ width: '100%', my: isMobile ? 1 : isDesktop ? 3 : 2 }}>
                        <Divider sx={{
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            '&::before, &::after': {
                                borderColor: 'rgba(255, 255, 255, 0.2)'
                            }
                        }}>
                            <Typography sx={{ color: '#888', px: 2, fontSize: isDesktop ? '1.5rem' : 'auto' }}>
                                ⸻
                            </Typography>
                        </Divider>
                    </Box>

                    {/* Stay Connected Section */}
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant={isMobile ? "h6" : isDesktop ? "h2" : "h4"}
                            sx={{
                                color: '#fff',
                                mb: isMobile ? 2 : isDesktop ? 4 : 3,
                                textAlign: 'center',
                                fontSize: isDesktop ? '3rem' : isMobile ? '1.5rem' : 'auto'
                            }}
                        >
                            Stay connected
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: isMobile ? 1 : isDesktop ? 3 : 2,
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%'
                            }}
                        >
                            {/* WhatsApp Button */}
                            <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<WhatsAppIcon />}
                                    href={socialLinks.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    fullWidth={isMobile}
                                    sx={{
                                        backgroundColor: '#25D366',
                                        color: '#fff',
                                        padding: isMobile ? '10px 16px' : isDesktop ? '16px 40px' : '12px 30px',
                                        fontSize: isDesktop ? '1.2rem' : isMobile ? '0.85rem' : '1rem',
                                        borderRadius: isMobile ? 2 : isDesktop ? 4 : 3,
                                        '&:hover': {
                                            backgroundColor: '#128C7E',
                                            transform: 'scale(1.02)',
                                            transition: 'all 0.3s ease'
                                        },
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        minWidth: isMobile ? 'auto' : isDesktop ? '350px' : '200px',
                                        width: isMobile ? '100%' : 'auto'
                                    }}
                                >
                                    Join our WhatsApp Community
                                </Button>
                            </Box>
                        </Box>

                        {isMobile && <br />}

                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: isMobile ? 1 : isDesktop ? 3 : 0
                            }}
                        >
                            <SocialMediaIconsRow
                                width={isMobile ? '100%' : isDesktop ? '60%' : '50%'}
                                spacing={isMobile ? 'space-around' : 'space-between'}
                                iconSize={isMobile ? 40 : isDesktop ? 70 : 50}
                            />
                        </Box>
                    </Box>

                    {/* Footer Note */}
                    <Box sx={{ width: '100%', mt: isMobile ? 2 : isDesktop ? 4 : 3 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#666',
                                display: 'block',
                                textAlign: 'center',
                                fontSize: isDesktop ? '1rem' : isMobile ? '0.5rem' : '0.75rem'
                            }}
                        >
                            Camping In The Skies © {new Date().getFullYear()} - All rights reserved
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};